import { useNavigate } from '@tanstack/react-router';
import { Command, useCommandState } from 'cmdk';

import { ProductMessage } from '../../../../types/ProductMessage';
import { useProductsPagination } from '../../../product/overview/hooks/useProductsPagination';
import { useDebounce } from '../../hooks/useDebounce';
import { useAutoSelectFirstCommandItem } from '../hooks/useAutoSelectFirstCommandItem';
import { useShortcutMenu } from '../hooks/useShortcutMenu';

import { CommandEmptyState } from './CommandEmptyState';
import { CommandErrorState } from './CommandErrorState';
import { CommandItem } from './CommandItem';
import { CommandSkeletonList } from './CommandSkeletonList';

export const ProductSearch = () => {
    const search = useCommandState((state) => state.search);
    const debouncedSearch = useDebounce(search, 300);
    const { setOpen } = useShortcutMenu();

    const query = useProductsPagination({ searchQuery: debouncedSearch, page: 0 });

    useAutoSelectFirstCommandItem(query.data?.content[0]?.id);

    const navigate = useNavigate();

    const onProductSelect = () => {
        navigate({ to: '/products', search: { searchQuery: debouncedSearch, page: 0 } });
        setOpen(false);
    };

    if (query.isPending) {
        return <CommandSkeletonList />;
    }

    if (query.isError) {
        return <CommandErrorState />;
    }

    if (!query.data || query.data.content.length === 0) {
        return <CommandEmptyState />;
    }

    const formatProduct = (product: ProductMessage) => {
        const parts = [product.name];

        if (product.supplierProductGroup) {
            parts.push(`(${product.supplierProductGroup})`);
        }

        return parts.join(' ');
    };

    return (
        <Command.List>
            {query.data.content.map((product: ProductMessage) => (
                <CommandItem key={product.id} value={product.id} onSelect={onProductSelect}>
                    {formatProduct(product)}
                </CommandItem>
            ))}
        </Command.List>
    );
};
